import React, { useState, useEffect, useCallback } from 'react';
import { 
  Search, Plus, Edit, Trash2, User, Settings, FileText, CheckCircle, 
  Clock, AlertTriangle, UserPlus, Laptop, Upload, Download, X, Wrench 
} from 'lucide-react';
import ApiService from './services/api';

const LoanerLaptopApp = () => {
  // State management
  const [currentUser, setCurrentUser] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [items, setItems] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);
  const [loans, setLoans] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [loanFilterBorrower, setLoanFilterBorrower] = useState('all');
  const [loanFilterStatus, setLoanFilterStatus] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [lastSubmittedApplication, setLastSubmittedApplication] = useState(null);

  // Data loading functions
  const loadItems = useCallback(async () => {
    try {
      const data = await ApiService.getItems();
      setItems(data);
    } catch (error) {
      console.error('Failed to load items:', error);
      setError('Failed to load items: ' + error.message);
    }
  }, []);

  const loadAvailableItems = useCallback(async () => {
    try {
      const data = await ApiService.getItems();
      const available = data.filter(item => item.status === 'available');
      setAvailableItems(available);
    } catch (error) {
      console.error('Failed to load available items:', error);
      setError('Failed to load available items: ' + error.message);
    }
  }, []);

  const loadLoans = useCallback(async () => {
    try {
      const data = await ApiService.getLoans();
      setLoans(data);
    } catch (error) {
      console.error('Failed to load loans:', error);
      setError('Failed to load loans: ' + error.message);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const data = await ApiService.getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Failed to load users:', error);
      setError('Failed to load users: ' + error.message);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const data = await ApiService.getCategories();
      setCategories(data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  }, []);

  const loadLocations = useCallback(async () => {
    try {
      const data = await ApiService.getLocations();
      setLocations(data);
    } catch (error) {
      console.error('Failed to load locations:', error);
    }
  }, []);

  const loadDashboardStats = useCallback(async () => {
    try {
      const data = await ApiService.getDashboardStats();
      setDashboardStats(data);
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
      setError('Failed to load dashboard stats: ' + error.message);
    }
  }, []);

  // Load initial data after login
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadItems(),
        loadLoans(),
        loadDashboardStats(),
        loadCategories(),
        loadLocations()
      ]);
      
      const user = await ApiService.getUserProfile();
      if (user.role === 'admin') {
        await loadUsers();
      }

      if (user.role === 'admin' || user.role === 'staff') {
        await loadAvailableItems();
      }
    } catch (error) {
      console.error('Failed to load initial data:', error);
      setError('Failed to load data: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [loadItems, loadLoans, loadDashboardStats, loadCategories, loadLocations, loadUsers, loadAvailableItems]);

  // Check for existing login on app start
  useEffect(() => {
    const checkAuthAndLoadData = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const user = await ApiService.getUserProfile();
          setCurrentUser(user);
          await loadInitialData();
        } catch {
          ApiService.logout();
        }
      }
    };
    
    checkAuthAndLoadData();
  }, [loadInitialData]);

  // Authentication
  const login = async (username, password) => {
    try {
      setLoading(true);
      setError('');
      const response = await ApiService.login(username, password);
      setCurrentUser(response.user);
      await loadInitialData();
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      setError(error.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    ApiService.logout();
    setCurrentUser(null);
    setItems([]);
    setAvailableItems([]);
    setLoans([]);
    setUsers([]);
    setDashboardStats({});
    setActiveTab('dashboard');
    setError('');
  };

  // Item management
  const addItem = async (newItem) => {
    try {
      setLoading(true);
      const itemData = {
        asset_id: newItem.assetId,
        rcb_sticker_number: newItem.rcbStickerNumber,
        type: newItem.type,
        category_id: newItem.categoryId,
        brand: newItem.brand,
        model: newItem.model,
        serial_number: newItem.serialNumber,
        purchase_date: newItem.purchaseDate,
        purchase_price: newItem.purchasePrice,
        warranty_expiry: newItem.warrantyExpiry,
        condition: newItem.condition,
        location_id: newItem.locationId,
        notes: newItem.notes,
        specifications: newItem.specifications,
        parts_used: newItem.partsUsed,
        can_leave_building: newItem.canLeaveBuilding !== undefined ? newItem.canLeaveBuilding : 1
      };
      
      await ApiService.createItem(itemData);
      await loadItems();
      await loadAvailableItems();
      await loadDashboardStats();
    } catch (error) {
      console.error('Failed to add item:', error);
      setError('Failed to add item: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const updateItem = async (id, updatedItem) => {
    try {
      setLoading(true);
      const itemData = {
        asset_id: updatedItem.assetId || updatedItem.asset_id,
        rcb_sticker_number: updatedItem.rcbStickerNumber || updatedItem.rcb_sticker_number,
        type: updatedItem.type,
        category_id: updatedItem.categoryId || updatedItem.category_id,
        brand: updatedItem.brand,
        model: updatedItem.model,
        serial_number: updatedItem.serialNumber || updatedItem.serial_number,
        purchase_date: updatedItem.purchaseDate || updatedItem.purchase_date,
        purchase_price: updatedItem.purchasePrice || updatedItem.purchase_price,
        warranty_expiry: updatedItem.warrantyExpiry || updatedItem.warranty_expiry,
        status: updatedItem.status,
        condition: updatedItem.condition,
        location_id: updatedItem.locationId || updatedItem.location_id,
        notes: updatedItem.notes,
        specifications: updatedItem.specifications,
        parts_used: updatedItem.partsUsed || updatedItem.parts_used,
        can_leave_building: updatedItem.canLeaveBuilding !== undefined ? updatedItem.canLeaveBuilding : updatedItem.can_leave_building
      };
      
      await ApiService.updateItem(id, itemData);
      await loadItems();
      await loadAvailableItems();
      await loadDashboardStats();
    } catch (error) {
      console.error('Failed to update item:', error);
      setError('Failed to update item: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteItem = async (id) => {
    try {
      setLoading(true);
      await ApiService.deleteItem(id);
      await loadItems();
      await loadAvailableItems();
      await loadDashboardStats();
    } catch (error) {
      console.error('Failed to delete item:', error);
      setError('Failed to delete item: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const importItems = async (file) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch('http://localhost:5000/api/items/import', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Import failed');
      }

      const result = await response.json();
      await loadItems();
      await loadAvailableItems();
      await loadDashboardStats();
      
      setError(`Import completed: ${result.imported} items imported, ${result.errors.length} errors`);
    } catch (error) {
      console.error('Failed to import items:', error);
      setError('Failed to import items: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Loan management
  const createLoanRequest = async (request) => {
    try {
      setLoading(true);
      const loanData = {
        item_id: parseInt(request.itemId),
        expected_return: request.expectedReturn,
        reason: request.reason
      };
      
      await ApiService.createLoanRequest(loanData);
      await loadLoans();
      await loadDashboardStats();
    } catch (error) {
      console.error('Failed to create loan request:', error);
      setError('Failed to create loan request: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // NEW: Direct user creation function for loaner applications
  const createUserForLoanerApp = async (userData) => {
    try {
      // Create user data with system defaults for loaner applications
      const newUserData = {
        username: userData.email.split('@')[0] || userData.panther_id,
        password: 'TempGSU2024!', // Temporary password
        name: userData.name,
        email: userData.email,
        role: 'borrower',
        panther_id: userData.panther_id,
        phone: userData.phone,
        user_type: userData.user_type,
        created_by_staff: true, // Flag to indicate staff creation
        requires_password_reset: true // Force password reset on first login
      };

      // Use a special endpoint for staff-created users that bypasses admin-only restriction
      const response = await fetch('http://localhost:5000/api/users/create-for-loaner', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newUserData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create user');
      }

      const result = await response.json();
      
      // Reload users list if admin
      if (currentUser.role === 'admin') {
        await loadUsers();
      }
      
      return result;
    } catch (error) {
      console.error('Error creating user for loaner app:', error);
      throw error;
    }
  };

  // NEW: Enhanced loan request creation for staff applications (auto-approved)
  const createStaffLoanRequest = async (loanData) => {
    try {
      const response = await fetch('http://localhost:5000/api/loans/staff-create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          ...loanData,
          auto_approve: true // Flag to auto-approve staff-created loans
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create loan request');
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to create staff loan request:', error);
      throw error;
    }
  };

  const approveLoan = async (loanId) => {
    try {
      setLoading(true);
      await ApiService.approveLoan(loanId);
      await loadLoans();
      await loadItems();
      await loadAvailableItems();
      await loadDashboardStats();
    } catch (error) {
      console.error('Failed to approve loan:', error);
      setError('Failed to approve loan: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const denyLoan = async (loanId) => {
    try {
      setLoading(true);
      await ApiService.denyLoan(loanId);
      await loadLoans();
      await loadDashboardStats();
    } catch (error) {
      console.error('Failed to deny loan:', error);
      setError('Failed to deny loan: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const returnItem = async (loanId, returnData = {}) => {
    try {
      setLoading(true);
      await ApiService.returnItem(loanId, returnData);
      await loadLoans();
      await loadItems();
      await loadAvailableItems();
      await loadDashboardStats();
    } catch (error) {
      console.error('Failed to return item:', error);
      setError('Failed to return item: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // User management
  const addUser = async (userData) => {
    try {
      setLoading(true);
      await ApiService.register(userData);
      await loadUsers();
    } catch (error) {
      console.error('Failed to add user:', error);
      setError('Failed to add user: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId) => {
    try {
      setLoading(true);
      await ApiService.deleteUser(userId);
      await loadUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      setError('Failed to delete user: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Modal functionality with better loading states
  const showStatsModal = async (type) => {
    // Show loading immediately for async operations
    if (type === 'overdueItems') {
      setModalContent(
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading overdue items...</p>
        </div>
      );
      setShowModal(true);
    }
    
    let content = null;
    
    try {
      switch (type) {
        case 'totalItems':
          content = <ItemsModal items={items} title="All Items" />;
          setModalContent(content);
          setShowModal(true);
          break;
          
        case 'availableItems':
          const availableItemsFiltered = items.filter(item => item.status === 'available');
          content = <ItemsModal items={availableItemsFiltered} title="Available Items" />;
          setModalContent(content);
          setShowModal(true);
          break;
          
        case 'loanedItems':
          const activeLoans = loans.filter(loan => loan.status === 'active');
          content = <LoansModal loans={activeLoans} title="Active Loans" />;
          setModalContent(content);
          setShowModal(true);
          break;
          
        case 'overdueItems':
          try {
            const overdueLoans = await ApiService.getOverdueLoans();
            if (!overdueLoans || overdueLoans.length === 0) {
              content = (
                <div className="text-center py-8">
                  <div className="text-green-600 text-6xl mb-4">✅</div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">No Overdue Items!</h3>
                  <p className="text-gray-600">All items are returned on time or within the expected return period.</p>
                </div>
              );
            } else {
              content = <LoansModal loans={overdueLoans} title="Overdue Items" />;
            }
            setModalContent(content);
            setShowModal(true);
          } catch (error) {
            console.error('Error loading overdue items:', error);
            setModalContent(
              <div className="text-center py-8">
                <div className="text-red-600 text-6xl mb-4">❌</div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Error Loading Data</h3>
                <p className="text-gray-600">Failed to load overdue items. Please try again.</p>
              </div>
            );
            setShowModal(true);
          }
          break;
          
        case 'maintenanceItems':
          const maintenanceItems = items.filter(item => item.status === 'maintenance');
          if (maintenanceItems.length === 0) {
            content = (
              <div className="text-center py-8">
                <div className="text-blue-600 text-6xl mb-4">🔧</div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">No Items in Maintenance</h3>
                <p className="text-gray-600">All equipment is currently in working condition.</p>
              </div>
            );
          } else {
            content = <MaintenanceItemsModal items={maintenanceItems} title="Items in Maintenance" onStatusChange={updateItem} />;
          }
          setModalContent(content);
          setShowModal(true);
          break;
          
        default:
          return;
      }
    } catch (error) {
      console.error('Error in showStatsModal:', error);
      setModalContent(
        <div className="text-center py-8">
          <div className="text-red-600 text-6xl mb-4">❌</div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Error</h3>
          <p className="text-gray-600">Something went wrong. Please try again.</p>
        </div>
      );
      setShowModal(true);
    }
  };

  // Get unique item types for filter
  const getItemTypes = () => {
    const types = [...new Set(items.map(item => item.type))];
    return types.sort();
  };

  // Error display component
  const ErrorDisplay = () => {
    if (!error) return null;
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
        <div className="flex justify-between items-center">
          <span>{error}</span>
          <button 
            onClick={() => setError('')}
            className="text-red-700 hover:text-red-900 text-xl font-bold"
          >
            ×
          </button>
        </div>
      </div>
    );
  };

  // Loading spinner component
  const LoadingSpinner = () => (
    <div className="flex justify-center items-center py-4">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
    </div>
  );

  // Modal components
  const Modal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          {/* Background overlay */}
          <div 
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={onClose}
          ></div>

          {/* Modal panel */}
          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {title}
                </h3>
                <button 
                  onClick={onClose}
                  className="bg-white rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <span className="sr-only">Close</span>
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ItemsModal = ({ items, title }) => (
    <div>
      <div className="mb-4">
        <p className="text-gray-600">Total: {items.length} items</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-gray-200">
          <thead>
            <tr className="bg-gray-50">
              <th className="border border-gray-200 px-4 py-2 text-left">Asset ID</th>
              <th className="border border-gray-200 px-4 py-2 text-left">RCB Sticker</th>
              <th className="border border-gray-200 px-4 py-2 text-left">Type</th>
              <th className="border border-gray-200 px-4 py-2 text-left">Brand/Model</th>
              <th className="border border-gray-200 px-4 py-2 text-left">Status</th>
              <th className="border border-gray-200 px-4 py-2 text-left">Location</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.id}>
                <td className="border border-gray-200 px-4 py-2 font-medium">{item.asset_id}</td>
                <td className="border border-gray-200 px-4 py-2 text-blue-600">{item.rcb_sticker_number || '-'}</td>
                <td className="border border-gray-200 px-4 py-2">{item.type}</td>
                <td className="border border-gray-200 px-4 py-2">{item.brand} {item.model}</td>
                <td className="border border-gray-200 px-4 py-2">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    item.status === 'available' ? 'bg-green-100 text-green-800' :
                    item.status === 'loaned' ? 'bg-yellow-100 text-yellow-800' :
                    item.status === 'maintenance' ? 'bg-blue-100 text-blue-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {item.status}
                  </span>
                </td>
                <td className="border border-gray-200 px-4 py-2">{item.location_name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const LoansModal = ({ loans, title }) => (
    <div>
      <div className="mb-4">
        <p className="text-gray-600">Total: {loans.length} loans</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-gray-200">
          <thead>
            <tr className="bg-gray-50">
              <th className="border border-gray-200 px-4 py-2 text-left">Asset ID</th>
              <th className="border border-gray-200 px-4 py-2 text-left">Borrower</th>
              <th className="border border-gray-200 px-4 py-2 text-left">Status</th>
              <th className="border border-gray-200 px-4 py-2 text-left">Expected Return</th>
              <th className="border border-gray-200 px-4 py-2 text-left">Approved By</th>
            </tr>
          </thead>
          <tbody>
            {loans.map(loan => (
              <tr key={loan.id}>
                <td className="border border-gray-200 px-4 py-2 font-medium">{loan.asset_id}</td>
                <td className="border border-gray-200 px-4 py-2">{loan.borrower_name}</td>
                <td className="border border-gray-200 px-4 py-2">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    loan.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    loan.status === 'active' ? 'bg-green-100 text-green-800' :
                    loan.status === 'returned' ? 'bg-blue-100 text-blue-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {loan.status}
                  </span>
                </td>
                <td className="border border-gray-200 px-4 py-2">{loan.expected_return}</td>
                <td className="border border-gray-200 px-4 py-2">{loan.approved_by_name || 'N/A'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const MaintenanceItemsModal = ({ items, title, onStatusChange }) => (
    <div>
      <div className="mb-4">
        <p className="text-gray-600">Total: {items.length} items</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-gray-200">
          <thead>
            <tr className="bg-gray-50">
              <th className="border border-gray-200 px-4 py-2 text-left">Asset ID</th>
              <th className="border border-gray-200 px-4 py-2 text-left">RCB Sticker</th>
              <th className="border border-gray-200 px-4 py-2 text-left">Type</th>
              <th className="border border-gray-200 px-4 py-2 text-left">Brand/Model</th>
              <th className="border border-gray-200 px-4 py-2 text-left">Condition</th>
              <th className="border border-gray-200 px-4 py-2 text-left">Location</th>
              <th className="border border-gray-200 px-4 py-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.id}>
                <td className="border border-gray-200 px-4 py-2 font-medium">{item.asset_id}</td>
                <td className="border border-gray-200 px-4 py-2 text-blue-600">{item.rcb_sticker_number || '-'}</td>
                <td className="border border-gray-200 px-4 py-2">{item.type}</td>
                <td className="border border-gray-200 px-4 py-2">{item.brand} {item.model}</td>
                <td className="border border-gray-200 px-4 py-2 capitalize">{item.condition}</td>
                <td className="border border-gray-200 px-4 py-2">{item.location_name}</td>
                <td className="border border-gray-200 px-4 py-2">
                  <button
                    onClick={() => onStatusChange(item.id, { ...item, status: 'available' })}
                    disabled={loading}
                    className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600 disabled:opacity-50"
                  >
                    Mark Available
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Login Component
  const LoginForm = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');

    const handleLogin = async () => {
      await login(username, password);
    };

    const handleKeyPress = (e) => {
      if (e.key === 'Enter') {
        handleLogin();
      }
    };

    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md w-96">
          <h2 className="text-2xl font-bold mb-6 text-center">GSU Loaner System</h2>
          <div onKeyPress={handleKeyPress}>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
                disabled={loading}
                required
              />
            </div>
            <div className="mb-6">
              <label className="block text-gray-700 text-sm font-bold mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
                disabled={loading}
                required
              />
            </div>
            {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
            <button
              onClick={handleLogin}
              disabled={loading}
              className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 transition duration-200 disabled:opacity-50"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </div>
          <div className="mt-6 text-sm text-gray-600">
            <p>Demo accounts:</p>
            <p>Admin: admin / admin123</p>
            <p>Staff: staff / staff123</p>
            <p>Student: john.doe / user123</p>
          </div>
        </div>
      </div>
    );
  };

  // Dashboard Component
  const Dashboard = () => {
    const {
      totalItems = null,
      availableItems = null,
      loanedItems = null,
      maintenanceItems = null,
      overdueLoans = null
    } = dashboardStats;

    const pendingLoansList = loans.filter(loan => loan.status === 'pending');

    // Skeleton loader component
    const StatSkeleton = () => (
      <div className="animate-pulse flex space-x-4 items-center">
        <div className="rounded-full bg-gray-200 h-10 w-10" />
        <div className="h-6 bg-gray-200 rounded w-20" />
      </div>
    );

    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold">Dashboard</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-md cursor-pointer hover:shadow-xl hover:scale-105 active:shadow-lg active:scale-100 transition-all duration-200 transform"
               onClick={() => showStatsModal('totalItems')}>
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-full">
                <Settings className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Items</p>
                <p className="text-2xl font-bold text-gray-900">
                  {totalItems === null ? <StatSkeleton /> : totalItems}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md cursor-pointer hover:shadow-xl hover:scale-105 active:shadow-lg active:scale-100 transition-all duration-200 transform"
               onClick={() => showStatsModal('availableItems')}>
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-full">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Available</p>
                <p className="text-2xl font-bold text-gray-900">
                  {availableItems === null ? <StatSkeleton /> : availableItems}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md cursor-pointer hover:shadow-xl hover:scale-105 active:shadow-lg active:scale-100 transition-all duration-200 transform"
               onClick={() => showStatsModal('loanedItems')}>
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 rounded-full">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">On Loan</p>
                <p className="text-2xl font-bold text-gray-900">
                  {loanedItems === null ? <StatSkeleton /> : loanedItems}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md cursor-pointer hover:shadow-xl hover:scale-105 active:shadow-lg active:scale-100 transition-all duration-200 transform"
               onClick={() => showStatsModal('overdueItems')}>
            <div className="flex items-center">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Overdue</p>
                <p className="text-2xl font-bold text-gray-900">
                  {overdueLoans === null ? <StatSkeleton /> : overdueLoans}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md cursor-pointer hover:shadow-xl hover:scale-105 active:shadow-lg active:scale-100 transition-all duration-200 transform"
               onClick={() => showStatsModal('maintenanceItems')}>
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-full">
                <Wrench className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Maintenance</p>
                <p className="text-2xl font-bold text-gray-900">
                  {maintenanceItems === null ? <StatSkeleton /> : maintenanceItems}
                </p>
              </div>
            </div>
          </div>
        </div>
        {(currentUser.role === 'admin' || currentUser.role === 'staff') && pendingLoansList.length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold mb-4">Pending Loan Requests</h3>
            <div className="space-y-3">
              {pendingLoansList.map(loan => (
                <div key={loan.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div>
                    <p className="font-medium">{loan.borrower_name}</p>
                    <p className="text-sm text-gray-600">
                      Asset: {loan.asset_id} | Due: {loan.expected_return} | Type: {loan.application_type}
                    </p>
                    {loan.panther_id && (
                      <p className="text-sm text-gray-500">Panther ID: {loan.panther_id}</p>
                    )}
                    {loan.approved_by_name && (
                      <p className="text-sm text-blue-600">Approved by: {loan.approved_by_name}</p>
                    )}
                  </div>
                  <div className="space-x-2">
                    <button
                      onClick={() => approveLoan(loan.id)}
                      disabled={loading}
                      className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600 disabled:opacity-50"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => denyLoan(loan.id)}
                      disabled={loading}
                      className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 disabled:opacity-50"
                    >
                      Deny
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title="Details"
        >
          {modalContent}
        </Modal>
      </div>
    );
  };

  // Inventory Component
  const InventoryManagement = () => {
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [showImportModal, setShowImportModal] = useState(false);
    const [inventorySearch, setInventorySearch] = useState('');
    const [debouncedInventorySearch, setDebouncedInventorySearch] = useState('');

    // Debounce inventory search
    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedInventorySearch(inventorySearch);
      }, 300);
      return () => clearTimeout(handler);
    }, [inventorySearch]);

    // Updated filter logic to use debouncedInventorySearch
    const filteredItems = items.filter(item => {
      const searchText = debouncedInventorySearch.trim().toLowerCase();
      const fields = [
        item.asset_id,
        item.type,
        item.brand,
        item.model,
        item.serial_number,
        item.rcb_sticker_number,
        item.location_name,
        item.notes,
        item.specifications
      ];
      const matchesSearch = !searchText || fields.some(field =>
        field && field.toString().toLowerCase().includes(searchText)
      );
      const matchesStatusFilter = filterStatus === 'all' || item.status === filterStatus;
      const matchesTypeFilter = filterType === 'all' || item.type === filterType;
      return matchesSearch && matchesStatusFilter && matchesTypeFilter;
    });
    const itemTypes = getItemTypes();

    const ItemForm = ({ item, onSave, onCancel }) => {
      const [formData, setFormData] = useState(item || {
        assetId: '',
        rcbStickerNumber: '',
        type: 'Laptop',
        categoryId: 1,
        brand: '',
        model: '',
        serialNumber: '',
        purchaseDate: '',
        purchasePrice: '',
        warrantyExpiry: '',
        condition: 'excellent',
        locationId: 1,
        notes: '',
        specifications: '',
        partsUsed: '',
        canLeaveBuilding: false
      });

      const handleSubmit = async () => {
        try {
          await onSave(formData);
          onCancel();
        } catch (error) {
          // Error is already handled in the parent functions
        }
      };

      return (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-4">{item ? 'Edit Item' : 'Add New Item'}</h3>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Asset ID *</label>
                <input
                  type="text"
                  value={formData.assetId || formData.asset_id || ''}
                  onChange={(e) => setFormData({...formData, assetId: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">RCB Sticker Number</label>
                <input
                  type="text"
                  value={formData.rcbStickerNumber || formData.rcb_sticker_number || ''}
                  onChange={(e) => setFormData({...formData, rcbStickerNumber: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Type *</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="Laptop">Laptop</option>
                  <option value="Monitor">Monitor</option>
                  <option value="Charger">Charger</option>
                  <option value="Adapter">Adapter</option>
                  <option value="Tablet">Tablet</option>
                  <option value="Webcam">Webcam</option>
                  <option value="Clickers">Clickers</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Category</label>
                <select
                  value={formData.categoryId || formData.category_id || 1}
                  onChange={(e) => setFormData({...formData, categoryId: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Brand *</label>
                <input
                  type="text"
                  value={formData.brand}
                  onChange={(e) => setFormData({...formData, brand: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Model *</label>
                <input
                  type="text"
                  value={formData.model}
                  onChange={(e) => setFormData({...formData, model: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Serial Number *</label>
                <input
                  type="text"
                  value={formData.serialNumber || formData.serial_number || ''}
                  onChange={(e) => setFormData({...formData, serialNumber: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Purchase Date</label>
                <input
                  type="date"
                  value={formData.purchaseDate || formData.purchase_date || ''}
                  onChange={(e) => setFormData({...formData, purchaseDate: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Purchase Price</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.purchasePrice || formData.purchase_price || ''}
                  onChange={(e) => setFormData({...formData, purchasePrice: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Warranty Expiry</label>
                <input
                  type="date"
                  value={formData.warrantyExpiry || formData.warranty_expiry || ''}
                  onChange={(e) => setFormData({...formData, warrantyExpiry: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Condition</label>
                <select
                  value={formData.condition}
                  onChange={(e) => setFormData({...formData, condition: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="excellent">Excellent</option>
                  <option value="good">Good</option>
                  <option value="fair">Fair</option>
                  <option value="poor">Poor</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Location</label>
                <select
                  value={formData.locationId || formData.location_id || 1}
                  onChange={(e) => setFormData({...formData, locationId: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Specifications</label>
              <textarea
                value={formData.specifications || ''}
                onChange={(e) => setFormData({...formData, specifications: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                rows="2"
                placeholder="Technical specifications..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Parts Used</label>
              <textarea
                value={formData.partsUsed || formData.parts_used || ''}
                onChange={(e) => setFormData({...formData, partsUsed: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                rows="2"
                placeholder="Parts and accessories included..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Notes</label>
              <textarea
                value={formData.notes || ''}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                rows="2"
                placeholder="Additional notes..."
              />
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={!formData.canLeaveBuilding}
                  onChange={(e) => setFormData({...formData, canLeaveBuilding: !e.target.checked})}
                  id="cannotLeaveBuilding"
                />
                <label htmlFor="cannotLeaveBuilding" className="text-sm font-medium text-yellow-800">
                  🏢 Cannot leave Buckhead Center Building (Default: Checked)
                </label>
              </div>
              <p className="text-xs text-yellow-700 mt-1">
                Check this box for equipment that must remain on campus premises at all times
              </p>
            </div>
            
            <div className="flex space-x-2 pt-4">
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      );
    };

    const ImportModal = () => {
      const [file, setFile] = useState(null);

      const handleFileSelect = (e) => {
        setFile(e.target.files[0]);
      };

      const handleImport = async () => {
        if (file) {
          await importItems(file);
          setShowImportModal(false);
          setFile(null);
        }
      };

      return (
        <Modal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          title="Import Items from Excel"
        >
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Upload an Excel file with columns: Asset ID, RCB Sticker Number, Type, Brand, Model, Serial Number, Purchase Date, Purchase Price, Condition, Notes, Specifications, Parts Used
              </p>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleImport}
                disabled={!file || loading}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
              >
                {loading ? 'Importing...' : 'Import'}
              </button>
              <button
                onClick={() => setShowImportModal(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      );
    };

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">Inventory Management</h2>
          {(currentUser.role === 'admin' || currentUser.role === 'staff') && (
            <div className="space-x-2">
              <button
                onClick={() => setShowImportModal(true)}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50"
              >
                <Upload className="h-4 w-4 mr-2" />
                Import Excel
              </button>
              <button
                onClick={() => setShowAddForm(true)}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </button>
            </div>
          )}
        </div>

        {/* New Search Bar */}
        <div className="mb-4 flex items-center max-w-md">
          <div className="relative flex-1">
            <input
              type="text"
              value={inventorySearch}
              onChange={e => setInventorySearch(e.target.value)}
              placeholder="Search inventory..."
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="absolute left-3 top-2.5 text-gray-400">
              <Search className="h-5 w-5" />
            </span>
            {inventorySearch && (
              <button
                type="button"
                onClick={() => setInventorySearch('')}
                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        {/* Custom Filter Section */}
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Filter Dropdowns */}
            <div className="min-w-0 flex-1 lg:flex-none lg:w-40">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="block w-full py-3 px-3 border border-gray-300 bg-white rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="all">All Status</option>
                <option value="available">Available</option>
                <option value="loaned">On Loan</option>
                <option value="maintenance">Maintenance</option>
                <option value="faulty">Faulty</option>
              </select>
            </div>
            <div className="min-w-0 flex-1 lg:flex-none lg:w-40">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="block w-full py-3 px-3 border border-gray-300 bg-white rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="all">All Types</option>
                {itemTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>
          {/* Search Results Summary */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Showing <span className="font-semibold text-gray-900">{filteredItems.length}</span> of <span className="font-semibold text-gray-900">{items.length}</span> items
              {(filterStatus !== 'all' || filterType !== 'all' || debouncedInventorySearch) && (
                <span> with applied filters</span>
              )}
            </p>
          </div>
        </div>

        {showAddForm && (
          <ItemForm
            onSave={addItem}
            onCancel={() => setShowAddForm(false)}
          />
        )}

        {editingItem && (
          <ItemForm
            item={editingItem}
            onSave={(updatedItem) => updateItem(editingItem.id, updatedItem)}
            onCancel={() => setEditingItem(null)}
          />
        )}

        <ImportModal />

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Asset ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">RCB Sticker</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Brand/Model</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Condition</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                  {(currentUser.role === 'admin' || currentUser.role === 'staff') && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredItems.map(item => (
                  <tr key={item.id}>
                    <td className="px-6 py-4 whitespace-nowrap font-medium">{item.asset_id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">{item.rcb_sticker_number || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{item.type}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{item.brand} {item.model}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        item.status === 'available' ? 'bg-green-100 text-green-800' :
                        item.status === 'loaned' ? 'bg-yellow-100 text-yellow-800' :
                        item.status === 'maintenance' ? 'bg-blue-100 text-blue-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {item.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap capitalize">{item.condition}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        {item.location_name}
                        {!item.can_leave_building && (
                          <div className="text-xs text-red-600">No exit</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {item.purchase_price ? `$${item.purchase_price}` : 'N/A'}
                    </td>
                    {(currentUser.role === 'admin' || currentUser.role === 'staff') && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => setEditingItem(item)}
                          disabled={loading}
                          className="text-blue-600 hover:text-blue-900 mr-3 disabled:opacity-50"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        {currentUser.role === 'admin' && (
                          <button
                            onClick={() => deleteItem(item.id)}
                            disabled={loading}
                            className="text-red-600 hover:text-red-900 disabled:opacity-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Loan Management Component
  const LoanManagement = () => {
    const [showRequestForm, setShowRequestForm] = useState(false);
    const [loansSearch, setLoansSearch] = useState('');
    const [debouncedLoansSearch, setDebouncedLoansSearch] = useState('');

    // Debounce loans search
    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedLoansSearch(loansSearch);
      }, 300);
      return () => clearTimeout(handler);
    }, [loansSearch]);

    // Updated filter logic to use debouncedLoansSearch
    const filteredLoans = loans.filter(loan => {
      const searchText = debouncedLoansSearch.trim().toLowerCase();
      const fields = [
        loan.asset_id,
        loan.borrower_name,
        loan.reason,
        loan.panther_id,
        loan.type,
        loan.status,
        loan.approved_by_name,
        loan.returned_by_name
      ];
      const matchesSearch = !searchText || fields.some(field =>
        field && field.toString().toLowerCase().includes(searchText)
      );
      const matchesBorrowerFilter = loanFilterBorrower === 'all' || loan.borrower_id === parseInt(loanFilterBorrower);
      const matchesStatusFilter = loanFilterStatus === 'all' || loan.status === loanFilterStatus;
      return matchesSearch && matchesBorrowerFilter && matchesStatusFilter;
    });

    const RequestForm = () => {
      const [formData, setFormData] = useState({
        itemId: '',
        assetId: '',
        expectedReturn: '',
        reason: ''
      });

      const userAvailableItems = availableItems.filter(item => 
        currentUser.role === 'borrower' ? item.can_leave_building : true
      );

      const handleSubmit = async () => {
        try {
          await createLoanRequest(formData);
          setShowRequestForm(false);
          setFormData({ itemId: '', assetId: '', expectedReturn: '', reason: '' });
        } catch (error) {
          // Error is already handled in createLoanRequest
        }
      };

      return (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-4">Request Equipment Loan</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Select Item</label>
              <select
                value={formData.itemId}
                onChange={(e) => {
                  const selectedItem = userAvailableItems.find(item => item.id === parseInt(e.target.value));
                  setFormData({
                    ...formData,
                    itemId: e.target.value,
                    assetId: selectedItem ? selectedItem.asset_id : ''
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              >
                <option value="">Select an item...</option>
                {userAvailableItems.map(item => (
                  <option key={item.id} value={item.id}>
                    {item.asset_id} - {item.type} ({item.brand} {item.model})
                    {item.rcb_sticker_number && ` - RCB: ${item.rcb_sticker_number}`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Expected Return Date</label>
              <input
                type="date"
                value={formData.expectedReturn}
                onChange={(e) => setFormData({...formData, expectedReturn: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Reason for Request</label>
              <textarea
                value={formData.reason}
                onChange={(e) => setFormData({...formData, reason: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                rows="3"
                required
              />
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
              >
                {loading ? 'Submitting...' : 'Submit Request'}
              </button>
              <button
                type="button"
                onClick={() => setShowRequestForm(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">Loan Management</h2>
          {currentUser.role === 'borrower' && (
            <button
              onClick={() => setShowRequestForm(true)}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
            >
              <Plus className="h-4 w-4 mr-2" />
              Request Loan
            </button>
          )}
        </div>

        {/* New Search Bar */}
        <div className="mb-4 flex items-center max-w-md">
          <div className="relative flex-1">
            <input
              type="text"
              value={loansSearch}
              onChange={e => setLoansSearch(e.target.value)}
              placeholder="Search loans..."
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="absolute left-3 top-2.5 text-gray-400">
              <Search className="h-5 w-5" />
            </span>
            {loansSearch && (
              <button
                type="button"
                onClick={() => setLoansSearch('')}
                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        {/* Custom Filter Section */}
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Filter Dropdowns */}
            {(currentUser.role === 'admin' || currentUser.role === 'staff') && (
              <div className="min-w-0 flex-1 lg:flex-none lg:w-48">
                <select
                  value={loanFilterBorrower}
                  onChange={(e) => setLoanFilterBorrower(e.target.value)}
                  className="block w-full py-3 px-3 border border-gray-300 bg-white rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="all">All Borrowers</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>{user.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="min-w-0 flex-1 lg:flex-none lg:w-40">
              <select
                value={loanFilterStatus}
                onChange={(e) => setLoanFilterStatus(e.target.value)}
                className="block w-full py-3 px-3 border border-gray-300 bg-white rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="active">Active</option>
                <option value="returned">Returned</option>
                <option value="denied">Denied</option>
              </select>
            </div>
          </div>
          {/* Search Results Summary */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Showing <span className="font-semibold text-gray-900">{filteredLoans.length}</span> of <span className="font-semibold text-gray-900">{loans.length}</span> loan records
              {(loanFilterBorrower !== 'all' || loanFilterStatus !== 'all' || debouncedLoansSearch) && (
                <span> with applied filters</span>
              )}
            </p>
          </div>
        </div>

        {showRequestForm && <RequestForm />}

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Asset ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">RCB Sticker</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Borrower</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Request Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expected Return</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Approved By</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Returned By</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                  {(currentUser.role === 'admin' || currentUser.role === 'staff') && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLoans.map(loan => (
                  <tr key={loan.id}>
                    <td className="px-6 py-4 whitespace-nowrap font-medium">{loan.asset_id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">{loan.rcb_sticker_number || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="font-medium">{loan.borrower_name}</div>
                        {loan.panther_id && (
                          <div className="text-sm text-gray-500">ID: {loan.panther_id}</div>
                        )}
                        {loan.user_type && (
                          <div className="text-xs text-gray-500">{loan.user_type}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        loan.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        loan.status === 'active' ? 'bg-green-100 text-green-800' :
                        loan.status === 'returned' ? 'bg-blue-100 text-blue-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {loan.status}
                      </span>
                      {loan.application_type === 'staff_created' && (
                        <div className="text-xs text-purple-600 mt-1">Staff Created</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{loan.type}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{loan.request_date || loan.checkout_date}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{loan.expected_return}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{loan.approved_by_name || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{loan.returned_by_name || 'N/A'}</td>
                    <td className="px-6 py-4 max-w-xs truncate">{loan.reason}</td>
                    {(currentUser.role === 'admin' || currentUser.role === 'staff') && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {loan.status === 'pending' && currentUser.role === 'admin' && (
                          <div className="space-x-2">
                            <button
                              onClick={() => approveLoan(loan.id)}
                              disabled={loading}
                              className="text-green-600 hover:text-green-900 disabled:opacity-50"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => denyLoan(loan.id)}
                              disabled={loading}
                              className="text-red-600 hover:text-red-900 disabled:opacity-50"
                            >
                              Deny
                            </button>
                          </div>
                        )}
                        {loan.status === 'pending' && currentUser.role === 'staff' && (
                          <span className="text-gray-500 text-xs">Admin Only</span>
                        )}
                        {loan.status === 'active' && (
                          <button
                            onClick={() => returnItem(loan.id)}
                            disabled={loading}
                            className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                          >
                            Return
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // UPDATED: Enhanced Loaner Application Component with Fixed User Creation
  const LoanerApplicationForm = () => {
    const [showForm, setShowForm] = useState(false);
    const [showTerms, setShowTerms] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [itemSearchTerm, setItemSearchTerm] = useState('');
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showSearchResults, setShowSearchResults] = useState(false);
    const [showEmailNotification, setShowEmailNotification] = useState(false);
    const [formData, setFormData] = useState({
      name: '',
      email: '',
      panther_id: '',
      phone: '',
      user_type: 'student',
      item_ids: [],
      loan_date: new Date().toISOString().split('T')[0],
      expected_return: new Date().toISOString().split('T')[0],
      reason: '',
      borrower_signature: ''
    });

    // Search for existing users
    const searchUsers = async (searchTerm) => {
      if (searchTerm.length < 2) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }

      try {
        const filteredUsers = users.filter(user => 
          user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (user.panther_id && user.panther_id.toLowerCase().includes(searchTerm.toLowerCase()))
        );
        
        setSearchResults(filteredUsers);
        setShowSearchResults(true);
      } catch (error) {
        console.error('Error searching users:', error);
      }
    };

    // Select user from search results
    const selectUser = (user) => {
      setFormData({
        ...formData,
        name: user.name,
        email: user.email,
        panther_id: user.panther_id || '',
        phone: user.phone || '',
        user_type: user.user_type || 'student'
      });
      setUserSearchTerm(user.name);
      setShowSearchResults(false);
    };

    // Send email notification
    const sendEmailNotification = async () => {
      try {
        setLoading(true);
        const emailContent = `
Dear ${formData.name},

Your equipment loan application has been submitted successfully!

Application Details:
- Panther ID: ${formData.panther_id}
- Email: ${formData.email}
- Phone: ${formData.phone}
- User Type: ${formData.user_type}
- Equipment Count: ${formData.item_ids.length} items
- Expected Return: ${formData.expected_return}
- Reason: ${formData.reason}
- Signature: ${formData.borrower_signature}

Your application is pending admin approval. You will be notified once approved.

Best regards,
GSU Tech Support Team
        `;
        
        console.log('Email notification sent:', emailContent);
        setError('Email notification sent successfully to ' + formData.email);
        setShowEmailNotification(false);
      } catch (error) {
        setError('Failed to send email notification: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    const filteredAvailableItems = availableItems.filter(item => {
      const searchText = itemSearchTerm.toLowerCase();
      return (
        item.asset_id.toLowerCase().includes(searchText) ||
        item.type.toLowerCase().includes(searchText) ||
        item.brand.toLowerCase().includes(searchText) ||
        item.model.toLowerCase().includes(searchText) ||
        (item.rcb_sticker_number && item.rcb_sticker_number.toLowerCase().includes(searchText))
      );
    });

    const handleItemSelection = (itemId, selected) => {
      if (selected) {
        setFormData({
          ...formData,
          item_ids: [...formData.item_ids, parseInt(itemId)]
        });
      } else {
        setFormData({
          ...formData,
          item_ids: formData.item_ids.filter(id => id !== parseInt(itemId))
        });
      }
    };

    // Terms and Conditions Component
    const TermsAndConditions = () => (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-4xl max-h-96 overflow-y-auto">
          <div className="mb-6">
            <h2 className="text-xl font-bold text-center mb-2">
              Robinson College of Business – Buckhead Campus
            </h2>
            <h3 className="text-lg font-semibold text-center mb-4">
              Laptop & Device Loaning Program - Terms and Conditions
            </h3>
          </div>
          
          <div className="space-y-4 text-sm">
            <p className="font-medium">
              By signing below, you agree to the following terms and conditions regarding the temporary loan of laptops and other devices:
            </p>

            <div>
              <h4 className="font-semibold">1. Loan Eligibility</h4>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Devices are available only to authorized RCB students, faculty, or staff with valid identification.</li>
                <li>Devices must be checked out in person from the designated Tech Support Desk.</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold">2. On-Site Use Only</h4>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Devices may not be taken off the Buckhead Campus premises under any circumstances.</li>
                <li>Any attempt to remove a device from the property will be considered a violation of this agreement and may result in disciplinary action.</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold">3. Return Time Requirement</h4>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>All borrowed devices must be returned at least 30 minutes prior to the end of the business day.</li>
                <li>The exact return time will be communicated by the attending technician at the time of loan.</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold">4. Responsibility and Care</h4>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>You are fully responsible for the care and condition of the device during the loan period.</li>
                <li>Devices must not be left unattended or used in hazardous environments.</li>
                <li>Any damage, loss, or theft must be reported immediately to the Tech Support team.</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold">5. Usage Guidelines</h4>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Devices are intended for academic or professional use only.</li>
                <li>No software installations, administrative changes, or alterations to system settings are permitted.</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold">6. Penalties for Late Return or Misuse</h4>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Late returns may result in a temporary suspension of borrowing privileges.</li>
                <li>Repeated violations may lead to permanent revocation of access to loaned devices.</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold">7. Acknowledgment of Terms</h4>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>By borrowing a device, you acknowledge that you have read, understood, and agreed to abide by these terms.</li>
              </ul>
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-4">
            <button
              onClick={() => {
                setShowTerms(false);
              }}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Decline
            </button>
            <button
              onClick={() => {
                setShowTerms(false);
                setTermsAccepted(true);
              }}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
            >
              Accept Terms
            </button>
          </div>
        </div>
      </div>
    );

    const handleSubmit = async (e) => {
      e.preventDefault();
      
      if (!termsAccepted) {
        setError('You must accept the terms and conditions before proceeding');
        return;
      }
      
      if (!formData.name || !formData.email || !formData.panther_id || !formData.phone || 
          !formData.user_type || formData.item_ids.length === 0 || !formData.loan_date ||
          !formData.expected_return || !formData.reason || !formData.borrower_signature) {
        setError('Please fill in all required fields including your signature');
        return;
      }

      try {
        setLoading(true);
        
        // Check if user exists
        const existingUser = users.find(user => 
          user.email.toLowerCase() === formData.email.toLowerCase() ||
          user.panther_id === formData.panther_id
        );

        let createdUserId = null;

        // Create user if doesn't exist using the new function
        if (!existingUser) {
          try {
            const userResult = await createUserForLoanerApp(formData);
            createdUserId = userResult.id;
            console.log('User created successfully:', userResult);
          } catch (userError) {
            console.error('Failed to create user:', userError);
            setError('Failed to create user account: ' + userError.message);
            return;
          }
        } else {
          createdUserId = existingUser.id;
        }
        
        // Create loan requests for each selected item using staff endpoint
        const successfulLoans = [];
        const failedLoans = [];

        for (const itemId of formData.item_ids) {
          try {
            const loanData = {
              item_id: itemId,
              borrower_id: createdUserId,
              borrower_name: formData.name,
              borrower_email: formData.email,
              panther_id: formData.panther_id,
              phone: formData.phone,
              user_type: formData.user_type,
              expected_return: formData.expected_return,
              reason: formData.reason,
              application_type: 'staff_created'
            };
            
            await createStaffLoanRequest(loanData);
            successfulLoans.push(itemId);
          } catch (loanError) {
            console.error(`Failed to create loan for item ${itemId}:`, loanError);
            failedLoans.push(itemId);
          }
        }
        
        setLastSubmittedApplication({
          ...formData,
          successfulLoans: successfulLoans.length,
          failedLoans: failedLoans.length,
          createdNewUser: !existingUser
        });
        
        // Reload data
        await loadItems();
        await loadAvailableItems();
        await loadLoans();
        await loadDashboardStats();
        if (currentUser.role === 'admin') {
          await loadUsers();
        }
        
        setShowForm(false);
        setShowEmailNotification(true);
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          panther_id: '',
          phone: '',
          user_type: 'student',
          item_ids: [],
          loan_date: new Date().toISOString().split('T')[0],
          expected_return: new Date().toISOString().split('T')[0],
          reason: '',
          borrower_signature: ''
        });
        setTermsAccepted(false);
        setItemSearchTerm('');
        setUserSearchTerm('');
        setShowSearchResults(false);
        
        if (successfulLoans.length > 0) {
          setError(`Successfully created and APPROVED ${successfulLoans.length} loan application(s)${failedLoans.length > 0 ? ` (${failedLoans.length} failed)` : ''}! Equipment is ready for checkout.`);
        } else {
          setError('Failed to create any loan applications. Please try again.');
        }
      } catch (error) {
        console.error('Failed to create loaner application:', error);
        setError('Failed to create loaner application: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    if (showEmailNotification && lastSubmittedApplication) {
      return (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="text-center">
              <div className="text-green-600 text-6xl mb-4">✅</div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Application Created & Approved!</h2>
              <p className="text-gray-600 mb-6">
                The loaner application for <strong>{lastSubmittedApplication.name}</strong> has been created and <strong>automatically approved</strong>. Equipment is ready for immediate checkout!
              </p>
              
              <div className="bg-gray-50 p-4 rounded-lg mb-6 text-left">
                <h3 className="font-semibold mb-2">Application Summary:</h3>
                <p><strong>Name:</strong> {lastSubmittedApplication.name}</p>
                <p><strong>Email:</strong> {lastSubmittedApplication.email}</p>
                <p><strong>Panther ID:</strong> {lastSubmittedApplication.panther_id}</p>
                <p><strong>Successful Loans:</strong> {lastSubmittedApplication.successfulLoans}</p>
                {lastSubmittedApplication.failedLoans > 0 && (
                  <p className="text-red-600"><strong>Failed Loans:</strong> {lastSubmittedApplication.failedLoans}</p>
                )}
                <p><strong>Expected Return:</strong> {lastSubmittedApplication.expected_return}</p>
                <p><strong>Signature:</strong> {lastSubmittedApplication.borrower_signature}</p>
                {lastSubmittedApplication.createdNewUser && (
                  <p className="text-blue-600"><strong>New User Account:</strong> Created successfully</p>
                )}
              </div>
              
              <div className="space-x-4">
                <button
                  onClick={sendEmailNotification}
                  disabled={loading}
                  className="px-6 py-3 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
                >
                  {loading ? 'Sending...' : '📧 Send Email Notification'}
                </button>
                <button
                  onClick={() => {
                    setShowEmailNotification(false);
                    setLastSubmittedApplication(null);
                  }}
                  className="px-6 py-3 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (!showForm) {
      return (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">GSU Loaner Applications</h2>
            <button
              onClick={() => setShowForm(true)}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Create Application
            </button>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold mb-4">Instructions</h3>
            <p className="text-gray-600 mb-4">
              Use this form to create loaner applications for students and GSU team members. 
              The system will automatically create loan requests for the selected equipment and create user accounts if they don't exist.
              <strong className="text-blue-600"> Staff-created applications are automatically approved and ready for immediate checkout.</strong>
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-medium text-gray-800">For Students:</h4>
                <p className="text-gray-600">Use @student.gsu.edu email addresses</p>
                <p className="text-gray-600">Panther ID format: 002XXXXXX</p>
              </div>
              <div>
                <h4 className="font-medium text-gray-800">For GSU Team:</h4>
                <p className="text-gray-600">Use @gsu.edu email addresses</p>
                <p className="text-gray-600">Any valid GSU Panther ID</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {showTerms && <TermsAndConditions />}
        
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">Create GSU Loaner Application</h2>
          <button
            onClick={() => setShowForm(false)}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>

        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md space-y-6">
          {/* 1. User Search and Information Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-blue-700 border-b border-blue-200 pb-2">
              🔍 Search User or Add New
            </h3>
            
            {/* User Search Bar */}
            <div className="mb-4 relative">
              <label className="block text-sm font-medium mb-1">Search Existing User</label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={userSearchTerm}
                  onChange={(e) => {
                    setUserSearchTerm(e.target.value);
                    searchUsers(e.target.value);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
                  placeholder="Search by name, email, or Panther ID..."
                />
              </div>
              
              {/* Search Results Dropdown */}
              {showSearchResults && searchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {searchResults.map(user => (
                    <div
                      key={user.id}
                      onClick={() => selectUser(user)}
                      className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                    >
                      <div className="font-medium text-gray-900">{user.name}</div>
                      <div className="text-sm text-gray-600">{user.email}</div>
                      {user.panther_id && (
                        <div className="text-xs text-gray-500">Panther ID: {user.panther_id}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {showSearchResults && searchResults.length === 0 && userSearchTerm.length >= 2 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                  <div className="px-4 py-3 text-gray-500">
                    No users found. Fill in the details below to create a new user.
                  </div>
                </div>
              )}
            </div>

            <h4 className="text-md font-medium mb-3 text-gray-700">User Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
                  required
                  placeholder="Enter full name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email Address *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
                  required
                  placeholder="@student.gsu.edu or @gsu.edu"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Panther ID *</label>
                <input
                  type="text"
                  value={formData.panther_id}
                  onChange={(e) => setFormData({...formData, panther_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
                  required
                  placeholder="002XXXXXX"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Phone Number *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
                  required
                  placeholder="(xxx) xxx-xxxx"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">User Type *</label>
                <select
                  value={formData.user_type}
                  onChange={(e) => setFormData({...formData, user_type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
                  required
                >
                  <option value="student">Student</option>
                  <option value="GSU Team">GSU Team Member</option>
                  <option value="faculty">Faculty</option>
                  <option value="staff">Staff</option>
                </select>
              </div>
            </div>
          </div>

          {/* 2. Equipment Selection Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-green-700 border-b border-green-200 pb-2">
              💻 Equipment Selection
            </h3>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Search Available Equipment</label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={itemSearchTerm}
                  onChange={(e) => setItemSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
                  placeholder="Search by Asset ID, RCB Sticker, Type, Brand, or Model..."
                />
              </div>
            </div>

            <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md">
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Select</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Asset ID</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">RCB Sticker</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Brand/Model</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredAvailableItems.map(item => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <input
                          type="checkbox"
                          checked={formData.item_ids.includes(item.id)}
                          onChange={(e) => handleItemSelection(item.id, e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-4 py-2 font-medium">{item.asset_id}</td>
                      <td className="px-4 py-2 text-blue-600">{item.rcb_sticker_number || '-'}</td>
                      <td className="px-4 py-2">{item.type}</td>
                      <td className="px-4 py-2">{item.brand} {item.model}</td>
                      <td className="px-4 py-2">{item.location_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredAvailableItems.length === 0 && (
                <div className="text-center py-4 text-gray-500">
                  No available equipment matches your search
                </div>
              )}
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Selected items: {formData.item_ids.length}
            </p>
          </div>

          {/* 3. Loan Details Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-purple-700 border-b border-purple-200 pb-2">
              📅 Loan Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Loan Date *</label>
                <input
                  type="date"
                  value={formData.loan_date}
                  onChange={(e) => setFormData({...formData, loan_date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Expected Return Date *</label>
                <input
                  type="date"
                  value={formData.expected_return}
                  onChange={(e) => setFormData({...formData, expected_return: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
                  required
                  min={formData.loan_date}
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium mb-1">Reason for Loan Request *</label>
              <textarea
                value={formData.reason}
                onChange={(e) => setFormData({...formData, reason: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
                rows="3"
                required
                placeholder="Please describe the purpose for borrowing this equipment..."
              />
            </div>
          </div>

          {/* 4. Signature Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-orange-700 border-b border-orange-200 pb-2">
              ✍️ Digital Signature
            </h3>
            <div className="bg-orange-50 border border-orange-200 rounded-md p-4">
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Borrower Signature *</label>
                <input
                  type="text"
                  value={formData.borrower_signature}
                  onChange={(e) => setFormData({...formData, borrower_signature: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
                  required
                  placeholder="Type your full name as digital signature"
                />
              </div>
              <p className="text-sm text-orange-700">
                By typing your name above, you are providing a legally binding digital signature and acknowledge that you agree to all terms and conditions.
              </p>
            </div>
          </div>

          {/* 5. Terms and Conditions Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-red-700 border-b border-red-200 pb-2">
              📋 Terms and Conditions
            </h3>
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <div className="flex items-center space-x-2 mb-3">
                <input
                  type="checkbox"
                  id="termsAccepted"
                  checked={termsAccepted}
                  onChange={(e) => {
                    // Prevent direct checking - must go through modal
                    e.preventDefault();
                    setShowTerms(true);
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                  required
                />
                <label htmlFor="termsAccepted" className="text-sm font-medium text-yellow-800 cursor-pointer">
                  I have read and accept the terms and conditions for equipment loan
                </label>
              </div>
              <button
                type="button"
                onClick={() => setShowTerms(true)}
                className="text-blue-600 hover:text-blue-800 text-sm underline"
              >
                📖 Read Full Terms and Conditions
              </button>
            </div>
          </div>

          {/* 6. Create Loan Application Section */}
          <div className="border-t pt-6">
            <div className="flex space-x-4">
              <button
                type="submit"
                disabled={loading || !termsAccepted || formData.item_ids.length === 0 || !formData.borrower_signature}
                className="px-6 py-3 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {loading ? 'Creating Application...' : `Create Loan Application (${formData.item_ids.length} items)`}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-6 py-3 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 font-medium"
              >
                Cancel
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              This will create and automatically approve individual loan requests for each selected item.
              Equipment will be ready for immediate checkout without requiring admin approval.
              {!users.find(user => user.email.toLowerCase() === formData.email.toLowerCase()) && formData.email && (
                <span className="text-blue-600"> A new user account will be created automatically.</span>
              )}
            </p>
          </div>
        </form>
      </div>
    );
  };

  // Reports Component
  const Reports = () => {
    const generateReport = (type) => {
      let data = [];
      let filename = '';

      switch (type) {
        case 'inventory':
          data = items.map(item => ({
            'Asset ID': item.asset_id,
            'RCB Sticker': item.rcb_sticker_number || '',
            'Type': item.type,
            'Brand': item.brand,
            'Model': item.model,
            'Serial Number': item.serial_number,
            'Status': item.status,
            'Condition': item.condition,
            'Location': item.location_name,
            'Purchase Price': item.purchase_price || 'N/A',
            'Purchase Date': item.purchase_date || 'N/A',
            'Warranty Expiry': item.warranty_expiry || 'N/A',
            'Specifications': item.specifications || 'N/A',
            'Parts Used': item.parts_used || 'N/A',
            'Can Leave Building': item.can_leave_building ? 'Yes' : 'No',
            'Notes': item.notes || 'N/A'
          }));
          filename = 'gsu_inventory_report.csv';
          break;
        case 'loans':
          data = loans.map(loan => ({
            'Asset ID': loan.asset_id,
            'RCB Sticker': loan.rcb_sticker_number || '',
            'Borrower': loan.borrower_name,
            'Panther ID': loan.panther_id || '',
            'User Type': loan.user_type || '',
            'Status': loan.status,
            'Application Type': loan.application_type,
            'Request Date': loan.request_date || loan.checkout_date,
            'Approved Date': loan.approved_date || 'N/A',
            'Expected Return': loan.expected_return,
            'Actual Return': loan.actual_return || 'N/A',
            'Approved By': loan.approved_by_name || 'N/A',
            'Returned By': loan.returned_by_name || 'N/A',
            'Reason': loan.reason,
            'Return Condition': loan.return_condition || 'N/A',
            'Return Notes': loan.return_notes || 'N/A'
          }));
          filename = 'gsu_loans_report.csv';
          break;
        case 'overdue':
          const overdueLoans = loans.filter(loan => {
            if (loan.status !== 'active') return false;
            const today = new Date();
            const returnDate = new Date(loan.expected_return);
            return today > returnDate;
          });
          data = overdueLoans.map(loan => ({
            'Asset ID': loan.asset_id,
            'RCB Sticker': loan.rcb_sticker_number || '',
            'Borrower': loan.borrower_name,
            'Panther ID': loan.panther_id || '',
            'Checkout Date': loan.checkout_date,
            'Expected Return': loan.expected_return,
            'Days Overdue': Math.floor((new Date() - new Date(loan.expected_return)) / (1000 * 60 * 60 * 24)),
            'Approved By': loan.approved_by_name || 'N/A',
            'Reason': loan.reason
          }));
          filename = 'gsu_overdue_report.csv';
          break;
        default:
          return;
      }

      // Convert to CSV format
      if (data.length > 0) {
        const headers = Object.keys(data[0]).join(',');
        const rows = data.map(row => Object.values(row).map(value => 
          `"${String(value).replace(/"/g, '""')}"`
        ).join(','));
        const csv = [headers, ...rows].join('\n');
        
        // Create and download file
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    };

    const overdueCount = loans.filter(loan => {
      if (loan.status !== 'active') return false;
      const today = new Date();
      const returnDate = new Date(loan.expected_return);
      return today > returnDate;
    }).length;

    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold">Reports</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold mb-4">Inventory Report</h3>
            <p className="text-gray-600 mb-4">Complete list of all inventory items with current status and GSU details.</p>
            <button
              onClick={() => generateReport('inventory')}
              className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
            >
              <Download className="h-4 w-4 mr-2" />
              Download CSV
            </button>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold mb-4">Loans Report</h3>
            <p className="text-gray-600 mb-4">All loan transactions including pending, active, and returned items with Panther IDs.</p>
            <button
              onClick={() => generateReport('loans')}
              className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
            >
              <Download className="h-4 w-4 mr-2" />
              Download CSV
            </button>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold mb-4">Overdue Items Report</h3>
            <p className="text-gray-600 mb-4">Items past their expected return date with staff details and GSU information.</p>
            <div className="flex items-center justify-between mb-4">
              <span className="text-2xl font-bold text-red-600">{overdueCount}</span>
              <span className="text-sm text-gray-500">overdue items</span>
            </div>
            <button
              onClick={() => generateReport('overdue')}
              className="flex items-center px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
            >
              <Download className="h-4 w-4 mr-2" />
              Download CSV
            </button>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-4">Quick Stats</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{dashboardStats.totalItems || 0}</p>
              <p className="text-sm text-gray-600">Total Items</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{dashboardStats.availableItems || 0}</p>
              <p className="text-sm text-gray-600">Available</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-yellow-600">{dashboardStats.activeLoans || 0}</p>
              <p className="text-sm text-gray-600">Active Loans</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">{dashboardStats.pendingLoans || 0}</p>
              <p className="text-sm text-gray-600">Pending Requests</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">{dashboardStats.maintenanceItems || 0}</p>
              <p className="text-sm text-gray-600">Maintenance</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // User Management Component
  const UserManagement = () => {
    const [showAddForm, setShowAddForm] = useState(false);
    const [userSearch, setUserSearch] = useState('');
    const [debouncedUserSearch, setDebouncedUserSearch] = useState('');
    const [editingUser, setEditingUser] = useState(null);
    const [editFormData, setEditFormData] = useState(null);

    // Debounce user search
    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedUserSearch(userSearch);
      }, 300);
      return () => clearTimeout(handler);
    }, [userSearch]);

    // Filter users by search
    const filteredUsers = users.filter(user => {
      const searchText = debouncedUserSearch.trim().toLowerCase();
      if (!searchText) return true;
      const fields = [
        user.username,
        user.name,
        user.email,
        user.panther_id,
        user.user_type,
        user.role
      ];
      return fields.some(field => field && field.toString().toLowerCase().includes(searchText));
    });

    // Edit user handlers
    const handleEditUser = (user) => {
      setEditingUser(user);
      setEditFormData({ ...user });
    };
    const handleEditChange = (e) => {
      const { name, value, type, checked } = e.target;
      setEditFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    };
    const handleEditSave = async () => {
      try {
        setLoading(true);
        // Prepare update payload
        const updateData = {
          name: editFormData.name,
          email: editFormData.email,
          role: editFormData.role,
          phone: editFormData.phone,
          user_type: editFormData.user_type,
          panther_id: editFormData.panther_id,
          active: editFormData.active ? 1 : 0
        };
        await ApiService.updateUser(editFormData.id, updateData);
        await loadUsers();
        setEditingUser(null);
        setEditFormData(null);
      } catch (error) {
        setError('Failed to update user: ' + error.message);
      } finally {
        setLoading(false);
      }
    };
    const handleEditCancel = () => {
      setEditingUser(null);
      setEditFormData(null);
    };

    const UserForm = () => {
      const [formData, setFormData] = useState({
        username: '',
        password: '',
        name: '',
        email: '',
        role: 'borrower',
        panther_id: '',
        phone: '',
        user_type: 'student'
      });

      const handleSubmit = async () => {
        try {
          await addUser(formData);
          setShowAddForm(false);
          setFormData({ 
            username: '', password: '', name: '', email: '', role: 'borrower',
            panther_id: '', phone: '', user_type: 'student'
          });
        } catch (error) {
          // Error is already handled in addUser
        }
      };

      return (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-4">Add New GSU User</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Username</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Password</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Full Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Panther ID</label>
                <input
                  type="text"
                  value={formData.panther_id}
                  onChange={(e) => setFormData({...formData, panther_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="borrower">Borrower</option>
                  <option value="staff">Staff</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">User Type</label>
                <select
                  value={formData.user_type}
                  onChange={(e) => setFormData({...formData, user_type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="student">Student</option>
                  <option value="GSU Team">GSU Team</option>
                  <option value="staff">Staff</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
              >
                {loading ? 'Adding...' : 'Add User'}
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      );
    };

    const handleDeleteUser = async (userId, userName) => {
      if (window.confirm(`Are you sure you want to deactivate user "${userName}"? This will prevent them from logging in but preserve all historical data.`)) {
        await deleteUser(userId);
      }
    };

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">User Management</h2>
          <button
            onClick={() => setShowAddForm(true)}
            disabled={loading}
            className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add User
          </button>
        </div>

        {/* User Search Bar */}
        <div className="mb-4 flex items-center max-w-md">
          <div className="relative flex-1">
            <input
              type="text"
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
              placeholder="Search users..."
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="absolute left-3 top-2.5 text-gray-400">
              <Search className="h-5 w-5" />
            </span>
            {userSearch && (
              <button
                type="button"
                onClick={() => setUserSearch('')}
                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        {showAddForm && <UserForm />}

        {/* Edit User Modal */}
        {editingUser && editFormData && (
          <Modal isOpen={true} onClose={handleEditCancel} title={`Edit User: ${editingUser.username}`}>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={editFormData.name}
                    onChange={handleEditChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={editFormData.email}
                    onChange={handleEditChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Panther ID</label>
                  <input
                    type="text"
                    name="panther_id"
                    value={editFormData.panther_id || ''}
                    onChange={handleEditChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={editFormData.phone || ''}
                    onChange={handleEditChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Role</label>
                  <select
                    name="role"
                    value={editFormData.role}
                    onChange={handleEditChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="borrower">Borrower</option>
                    <option value="staff">Staff</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">User Type</label>
                  <select
                    name="user_type"
                    value={editFormData.user_type}
                    onChange={handleEditChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="student">Student</option>
                    <option value="GSU Team">GSU Team</option>
                    <option value="staff">Staff</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="flex items-center space-x-2 mt-2">
                  <input
                    type="checkbox"
                    name="active"
                    checked={!!editFormData.active}
                    onChange={handleEditChange}
                    id="activeCheckbox"
                  />
                  <label htmlFor="activeCheckbox" className="text-sm font-medium">Active</label>
                </div>
              </div>
              <div className="flex space-x-2 pt-4">
                <button
                  onClick={handleEditSave}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={handleEditCancel}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </div>
          </Modal>
        )}

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Full Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Panther ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active Loans</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map(user => {
                  const userActiveLoans = loans.filter(loan => loan.borrower_id === user.id && loan.status === 'active').length;
                  return (
                    <tr key={user.id}>
                      <td className="px-6 py-4 whitespace-nowrap font-medium">{user.username}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{user.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{user.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{user.panther_id || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          user.role === 'admin' ? 'bg-red-100 text-red-800' :
                          user.role === 'staff' ? 'bg-blue-100 text-blue-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          user.user_type === 'student' ? 'bg-purple-100 text-purple-800' :
                          user.user_type === 'GSU Team' ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {user.user_type || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          user.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {user.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{userActiveLoans}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleEditUser(user)}
                          disabled={loading}
                          className="text-blue-600 hover:text-blue-900 mr-3 disabled:opacity-50"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        {user.active && user.id !== currentUser.id && (
                          <button
                            onClick={() => handleDeleteUser(user.id, user.name)}
                            disabled={loading}
                            className="text-red-600 hover:text-red-900 disabled:opacity-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Main Navigation
  const Navigation = () => {
    const tabs = [
      { id: 'dashboard', label: 'Dashboard', icon: Settings },
      { id: 'inventory', label: 'Inventory', icon: Search },
      { id: 'loans', label: 'Loans', icon: Clock },
      { id: 'reports', label: 'Reports', icon: FileText },
    ];

    if (currentUser.role === 'admin' || currentUser.role === 'staff') {
      tabs.splice(3, 0, { id: 'loaner-applications', label: 'Loaner Apps', icon: Laptop });
    }

    if (currentUser.role === 'admin') {
      tabs.push({ id: 'users', label: 'Users', icon: User });
    }

    return (
      <nav className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center py-4">
            <div className="flex space-x-8">
              <h1 className="text-xl font-bold text-gray-900">GSU Loaner System</h1>
              <div className="flex space-x-4">
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                        activeTab === tab.id
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <Icon className="h-4 w-4 mr-2" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Welcome, {currentUser.name}</span>
              <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">{currentUser.role}</span>
              {currentUser.user_type && (
                <span className="px-2 py-1 text-xs bg-blue-100 text-blue-600 rounded">{currentUser.user_type}</span>
              )}
              <button
                onClick={logout}
                className="text-sm text-red-600 hover:text-red-800"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>
    );
  };

  // Main App Component
  if (!currentUser) {
    return <LoginForm />;
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard />;
      case 'inventory':
        return <InventoryManagement />;
      case 'loans':
        return <LoanManagement />;
      case 'loaner-applications':
        return (currentUser.role === 'admin' || currentUser.role === 'staff') ? <LoanerApplicationForm /> : <Dashboard />;
      case 'reports':
        return (currentUser.role === 'admin' || currentUser.role === 'staff') ? <Reports /> : <Dashboard />;
      case 'users':
        return currentUser.role === 'admin' ? <UserManagement /> : <Dashboard />;
      default:
        return <Dashboard />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Navigation />
      <main className="max-w-7xl mx-auto py-6 px-4">
        <ErrorDisplay />
        {loading && <LoadingSpinner />}
        {renderContent()}
      </main>
    </div>
  );
};

export default LoanerLaptopApp;